export function getRadarChartSlug(label) {
  return (label || "")
    .toString()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .toLowerCase();
}

function orderRadarAxesClockwise(items) {
  const list = Array.isArray(items) ? items : [];
  const count = list.length;
  if (count <= 1) {
    return list;
  }
  // ECharts 5 radar advances axes anticlockwise from startAngle (default 90° = top).
  // Re-map so config order renders clockwise: first item at top, then to the right.
  return [list[0], ...list.slice(1).reverse()];
}

export function resolveRadarChartData({
  valuesMap = {},
  chartId = "RadarChart",
  dataRows = [],
  maxValue = 5,
}) {
  const safeData = Array.isArray(dataRows) ? dataRows : [];
  const numericMax = Number(maxValue);
  const axisMax = Number.isFinite(numericMax) && numericMax > 0 ? numericMax : 5;

  const indicators = safeData.map((row, idx) => {
    const fallbackLabel =
      row && typeof row.Label === "string" && row.Label.trim()
        ? row.Label.trim()
        : `Axis ${idx + 1}`;
    return {
      name: row?.Label || fallbackLabel,
      max: axisMax,
    };
  });

  const values = safeData.map((row) => parseFloat(row?.Value ?? 0) || 0);

  return { indicators, values, maxValue: axisMax };
}

export function seedRadarChartVariables(targetArr, { props, pageId, chartId: chartIdOverride }) {
  if (!props || !targetArr) {
    return;
  }

  const chartId = chartIdOverride || props.id || "RadarChart";
  const rows = Array.isArray(props.Data) ? props.Data : [];

  rows.forEach((row, idx) => {
    const fallbackLabel =
      row && typeof row.Label === "string" && row.Label.trim()
        ? row.Label.trim()
        : `Axis ${idx + 1}`;
    const labelKey = row?.LabelUuid || `${chartId}-row-${idx}-label`;
    const valueKey = row?.ValueUuid || `${chartId}-row-${idx}-value`;
    const labelSlug = getRadarChartSlug(fallbackLabel);
    const categoryKeyNamed = `${chartId}-${labelSlug}-category`;
    const radarValueKeyNamed = `${chartId}-${labelSlug}-radar_value`;
    const numericValue = parseFloat(row?.Value ?? 0) || 0;

    targetArr[labelKey] = {
      currency: "0",
      count: 0,
      required: false,
      type: "RadarChart-Label",
      actions: "",
      action_id: "",
      link: "",
      value: fallbackLabel,
      default_value: "",
      roi_page_id: pageId,
      text: fallbackLabel,
      name: fallbackLabel,
      variable_data_type: "string",
    };

    targetArr[categoryKeyNamed] = {
      currency: "0",
      count: 0,
      required: false,
      type: "RadarChart-Label",
      actions: "",
      action_id: "",
      link: "",
      value: fallbackLabel,
      default_value: "",
      roi_page_id: pageId,
      text: `${fallbackLabel} Category`,
      variable_data_type: "string",
    };

    targetArr[valueKey] = {
      currency: "0",
      count: 0,
      required: false,
      type: "RadarChart-Value",
      actions: "",
      action_id: "",
      link: "",
      value: numericValue,
      default_value: "",
      roi_page_id: pageId,
      text: `${fallbackLabel} Value`,
      variable_data_type: "number",
    };

    if (labelSlug) {
      targetArr[labelSlug] = {
        currency: "0",
        count: 0,
        required: false,
        type: "RadarChart-Value",
        actions: "",
        action_id: "",
        link: "",
        value: numericValue,
        default_value: "",
        roi_page_id: pageId,
        text: fallbackLabel,
        name: fallbackLabel,
        variable_data_type: "number",
      };
    }

    targetArr[radarValueKeyNamed] = {
      currency: "0",
      count: 0,
      required: false,
      type: "RadarChart-Value",
      actions: "",
      action_id: "",
      link: "",
      value: numericValue,
      default_value: "",
      roi_page_id: pageId,
      text: `${fallbackLabel} Radar Value`,
      name: `${fallbackLabel} Radar Value`,
      variable_data_type: "number",
    };
  });
}

export function buildRadarChartOption({
  indicators = [],
  values = [],
  lineColor = "#E73F3F",
  fillColor = "rgba(231, 63, 63, 0.25)",
  splitNumber = 5,
}) {
  const safeIndicators = orderRadarAxesClockwise(
    Array.isArray(indicators) ? indicators : []
  );
  const safeValues = orderRadarAxesClockwise(Array.isArray(values) ? values : []);

  const formatRadarTooltip = (params) => {
    const marker = params.marker || "";

    if (Array.isArray(params.value)) {
      return params.value
        .map((val, idx) => {
          const label = safeIndicators[idx]?.name ?? "";
          return `${marker} ${label}: <b>${val}</b>`;
        })
        .join("<br/>");
    }

    return `${marker} ${params.name}: <b>${params.value}</b>`;
  };

  return {
    backgroundColor: "#FFFFFF",
    tooltip: {
      trigger: "item",
      backgroundColor: "rgba(255, 255, 255, 0.96)",
      borderColor: "#E1E1E1",
      textStyle: { color: "#333333" },
      formatter: formatRadarTooltip,
    },
    radar: {
      center: ["50%", "54%"],
      radius: "62%",
      startAngle: 90,
      indicator: safeIndicators,
      splitNumber: Number(splitNumber) > 0 ? Number(splitNumber) : 5,
      axisName: {
        color: "#333333",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 16,
      },
      splitLine: {
        lineStyle: {
          color: "#E1E1E1",
          width: 1,
        },
      },
      splitArea: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: "#E1E1E1",
          width: 1,
        },
      },
    },
    series: [
      {
        type: "radar",
        symbol: "circle",
        symbolSize: 8,
        itemStyle: {
          color: lineColor,
          borderColor: "#FFFFFF",
          borderWidth: 1,
        },
        lineStyle: {
          color: lineColor,
          width: 2,
        },
        areaStyle: {
          color: fillColor,
        },
        data: [
          {
            value: safeValues,
          },
        ],
      },
    ],
  };
}
